/**
 * Base API Client
 * Configured Axios instance with authentication, error handling, and analytics tracking
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { applyUiLanguageHeader } from './uiLanguageHeader';
import { captureNetworkError } from '@/lib/error-tracking';
import { config as productConfig } from '@/src/config';
import { getAuthToken } from '@/lib/authFetch';
import { checkSessionTokenPresent, SessionEndedError } from '@/lib/sessionBroadcast';
import { handleImpersonationResponse, isImpersonating } from '@/lib/impersonation';

// Triveny users hold a PLATFORM JWT (token_type=platform). Many legacy
// app.tsx mount paths make healthcare calls (/api/cbos, /api/referrals,
// /api/organization-branding, /api/user/preferences) using the same
// apiClient — those will 401 because healthcare auth rejects platform
// tokens. We don't want each 401 to log the Triveny user out. The
// allowlist below identifies those expected-fail URL prefixes; the
// interceptor below skips both the refresh-attempt AND the
// localStorage cleanup / auth:logout dispatch for them.
//
// This is a temporary mitigation. The right long-term fix is to make
// app.tsx skip these calls when isTriveny.
const HEALTHCARE_ONLY_URL_PREFIXES: ReadonlyArray<string> = [
  '/api/cbos',
  '/api/organizations',
  '/api/organization-branding',
  '/api/user/preferences',
  '/api/referrals',
  '/api/data-permissions',
  '/api/programs',
  '/api/workflows',
  '/api/members',
  '/api/sandbox',
  // Healthcare-side staff directory (used by Sales Pipeline owner picker via
  // /api/users/all-staff). Backend get_current_user decodes with the
  // healthcare JWT_SECRET, so a Triveny platform JWT always 401s here.
  // Suppressing the 401 keeps the Triveny session alive — the picker just
  // shows an empty list.
  '/api/users',
  // CRM activities (email integrations, tracking, etc.) also live on the
  // healthcare backend and reject platform JWTs the same way.
  '/api/crm-activities',
];

function isHealthcareOnlyUrl(url: string | undefined): boolean {
  if (!url) return false;
  // Strip protocol+host if absolute, leave only the path
  const path = url.replace(/^https?:\/\/[^/]+/, '');
  return HEALTHCARE_ONLY_URL_PREFIXES.some((p) => path.startsWith(p));
}

// Get API base URLs from environment.
//
// CORE backend — product-aware:
//   - Triveny (app.triveny.ai): ms-api-hots.onrender.com. The Triveny
//     platform JWT is signed with PLATFORM_JWT_SECRET on this service;
//     calling weavein-api with the same token 401s because the secret
//     differs. Every /api/platform/sales/* and /api/platform/contacts
//     call from the frontend goes through this client, so it MUST route
//     to ms-api-hots on Triveny — otherwise the demo tenant returns zero
//     rows on every page despite the backend cbo_id + type-clash fixes
//     being deployed.
//   - WeaveIn: weavein-api.onrender.com (legacy unified backend).
//
// When VITE_WEAVEIN_API_URL is explicitly empty (''), use relative URLs
// for Vercel proxy. When undefined, fall back to the product's default.
const WEAVEIN_API_URL = (
  productConfig.weaveinApi?.baseUrl
  ?? productConfig.weaveinApi.baseUrl
  ?? 'https://weavein-api.onrender.com'
).trim();

// Referrals API - now consolidated into WeaveIn API
const REFERRALS_API_URL = (import.meta.env.VITE_REFERRALS_API_URL ?? import.meta.env.VITE_API_URL ?? 'https://weavein-api.onrender.com').trim();

// Extended config to track request timing
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
  /**
   * Already retried once with a freshly refreshed token (TRUS-668). A second
   * 401 is final for this request: refreshing again would loop forever.
   */
  _authRetried?: boolean;
}

// Create axios instance for CORE WeaveIn features (organizations, programs, workflows, communications, members, data)
// This is the WeaveIn API - the main backend for everything except referrals
export const apiClient: AxiosInstance = axios.create({
  baseURL: WEAVEIN_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for Trust Marketplace Referrals ONLY
// Only used for referrals and provider matching
export const referralsApiClient: AxiosInstance = axios.create({
  baseURL: REFERRALS_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token interceptor function (reusable)
// Check both auth_token (admin/CBO portal) and member_token (member/employer portal)
const addAuthToken = (config: ExtendedAxiosRequestConfig) => {
  // This window thinks it is signed in but the token is gone: another window
  // signed out (TRUS-638). Sign out here — once, see requestLocalSignOut — and
  // do not send the request. This is NOT the 401 path: nothing is cleared and
  // no auth:logout is dispatched, so it cannot restart the old logout loop.
  // The logout call itself carries its token explicitly and is exempt.
  if (!(config as any).skipAuthLogout && !checkSessionTokenPresent({ refreshing: isRefreshing })) {
    throw new SessionEndedError();
  }
  // Use the keychain-aware accessor, not a bare localStorage read: on the Tauri
  // desktop app the admin/CBO JWT lives in the OS keychain (primed into an
  // in-memory cache by lib/authFetch), NOT plaintext localStorage. Reading only
  // localStorage.auth_token sent every apiClient request (e.g. the paginated
  // GET /api/members that backs Members & cases) with no Authorization header,
  // so the backend answered 401 "Authentication required: provide Bearer token
  // or X-API-Key header" even for a signed-in super admin (TRUS-493). getAuthToken
  // falls back to localStorage on web, so this is a no-op there. member_token is
  // still checked for the member/employer portals.
  const token = getAuthToken() || localStorage.getItem('member_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add X-CBO-ID header for multi-tenant schema resolution
  // Only set if not already provided by the calling code (allows per-request override)
  if (!config.headers['X-CBO-ID']) {
    const cboId = localStorage.getItem('active_cbo_id');
    if (cboId) {
      config.headers['X-CBO-ID'] = cboId;
    }
  }
  // TRUS-446 — tell the backend which language to resolve tenant-authored
  // labels into (custom field names, form sections, picklist option labels).
  // Shared with the per-service axios clients, which have to set it too.
  applyUiLanguageHeader(config);
  // Add X-Reveal-Token header for data shield reveal sessions (stored in sessionStorage)
  const revealToken = sessionStorage.getItem('reveal_token');
  if (revealToken) {
    config.headers['X-Reveal-Token'] = revealToken;
  }
  // Add request timing metadata
  config.metadata = { startTime: Date.now() };
  return config;
};

// State for refresh-on-401: prevents concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  config: ExtendedAxiosRequestConfig;
}> = [];

// Single-flight guard for the global logout dispatch. The app fires many
// concurrent requests (notification polling, dashboard fan-out), so one dead
// session produced DOZENS of simultaneous 401s, each dispatching its own
// auth:logout. Collapse them into one so the session is torn down exactly
// once. Reset via resetAuthFailureState() when a session is (re-)established.
let authLogoutDispatched = false;

/**
 * Clear the single-flight logout guard. Call after a successful login or token
 * refresh so a LATER genuine session failure can still dispatch auth:logout.
 */
export const resetAuthFailureState = () => {
  authLogoutDispatched = false;
};

/**
 * Did this request actually carry a bearer token?
 *
 * A 401 on a request that sent NO Authorization header says nothing about
 * whether the session is valid — there was no credential to reject. Treating
 * those as auth failures is what turned a single 401 into a self-sustaining
 * logout loop (see the comment on the clear-auth block below).
 */
const requestHadBearerToken = (config?: ExtendedAxiosRequestConfig): boolean => {
  const raw = config?.headers?.Authorization ?? (config?.headers as any)?.authorization;
  return typeof raw === 'string' && raw.startsWith('Bearer ');
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(apiClient.request(config));
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

// Error handling interceptor function (reusable)
// Attempts token refresh on 401 before falling back to logout
// Also tracks errors to Datadog
const handleAuthError = async (error: AxiosError) => {
  const config = error.config as ExtendedAxiosRequestConfig | undefined;
  const duration = config?.metadata?.startTime
    ? Date.now() - config.metadata.startTime
    : undefined;

  // Track the error to Datadog
  if (error.response?.status && error.response.status >= 400) {
    captureNetworkError(
      {
        url: config?.url || 'unknown',
        method: config?.method?.toUpperCase() || 'unknown',
        status: error.response.status,
        statusText: error.response.statusText,
        duration,
      },
      error,
      {
        component: 'apiClient',
        action: 'api_request_failed',
      }
    );
  } else if (!error.response) {
    // Network error (no response)
    captureNetworkError(
      {
        url: config?.url || 'unknown',
        method: config?.method?.toUpperCase() || 'unknown',
        duration,
      },
      error,
      {
        component: 'apiClient',
        action: 'network_error',
      }
    );
  }

  // Never clear tokens on 5xx server errors — these are backend issues, not auth failures
  if (error.response?.status && error.response.status >= 500) {
    console.warn(`⚠️ Server error ${error.response.status} on ${config?.url} — NOT clearing auth tokens`);
    return Promise.reject(error);
  }

  // TRUS-747: while a super admin is viewing as another user, a 401 means the
  // session ended (back to the admin) and a 403 may be the read-only / never-
  // allowed policy (shown in the banner). Never refresh and never sign out:
  // there is no refresh token, and signing out would end the ADMIN's session.
  if (isImpersonating() && (error.response?.status === 401 || error.response?.status === 403)) {
    handleImpersonationResponse(error.response.status, error.response.data, config?.url);
    return Promise.reject(error);
  }

  if (error.response?.status === 401) {
    console.warn('🔐 API returned 401 Unauthorized:', config?.url);

    // Per-request opt-out: feature fetches that may 401 for non-auth reasons
    // (wrong product/endpoint) can set skipAuthLogout to avoid nuking the
    // session — the caller handles the error locally.
    if ((config as any)?.skipAuthLogout) {
      console.warn('  ↳ skipAuthLogout set, session preserved:', config?.url);
      return Promise.reject(error);
    }

    // Triveny mitigation: a platform JWT calling a healthcare-only endpoint
    // will always 401 (healthcare get_current_user decodes with JWT_SECRET,
    // platform tokens are signed with PLATFORM_JWT_SECRET). For a Triveny
    // user, the only AUTH-RELEVANT 401 is on the platform /me endpoint —
    // every other 401 is a healthcare endpoint we shouldn't have called.
    // Silently reject those so the session survives instead of trying to
    // maintain a whack-a-mole allowlist.
    //
    // The narrow legitimate-401 carve-out (the platform JWT actually
    // expiring) is handled by AuthContext.verifyToken via /me — that path
    // doesn't go through this interceptor for the clear-tokens decision.
    if (productConfig.isTriveny) {
      const path = (config?.url ?? '').replace(/^https?:\/\/[^/]+/, '');
      const isPlatformAuthMe = path.includes('/api/platform/auth/me');
      if (!isPlatformAuthMe) {
        if (!isHealthcareOnlyUrl(config?.url)) {
          // Log as a hint so we eventually move callers to a Triveny-aware
          // endpoint — but don't clear the session over it.
          console.warn('  ↳ Triveny: 401 on non-allowlisted URL, session preserved:', path);
        }
        return Promise.reject(error);
      }
    }

    const isTokenVerification = config?.url?.includes('/auth/me') || config?.url?.includes('/user/preferences');
    const isRefreshCall = config?.url?.includes('/auth/refresh');

    // A 401 on a request that carried NO bearer token is not a verdict on the
    // session — there was no credential for the server to reject (the backend
    // logs these as "No authorization header provided"). Bailing out here is
    // what breaks the logout loop: once the first clear removed the token,
    // every in-flight and subsequent request went out header-less, and each of
    // those 401s cleared again and re-dispatched auth:logout — which logged
    // users out roughly one second after they signed back in.
    if (!requestHadBearerToken(config)) {
      console.warn('  ↳ 401 with no bearer token on the request, session untouched:', config?.url);
      return Promise.reject(error);
    }

    // Whether the session is genuinely beyond recovery. Only a definitive
    // rejection of the refresh token (or having none at all) qualifies; a
    // transient refresh failure must NOT tear the session down.
    let sessionUnrecoverable = false;

    // The request already went out again with a freshly refreshed token and
    // still got 401: the refresh cannot fix it (TRUS-668). Before this guard
    // the refresh-and-retry ran again on every such 401, an endless loop: one
    // background tab sent /api/notifications/summary and /api/messaging/calls/active
    // several times a second for 45 minutes. Fail this request; the poller's
    // own backoff takes over, and the session is left as it is.
    if (config?._authRetried) {
      console.warn('  ↳ 401 again after a token refresh, not refreshing again:', config?.url);
      return Promise.reject(error);
    }

    // Attempt token refresh for non-verification, non-refresh 401s
    if (!isRefreshCall && !isTokenVerification && config) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          config._authRetried = true;
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config });
          });
        }

        isRefreshing = true;
        try {
          const baseUrl = WEAVEIN_API_URL;
          const response = await fetch(`${baseUrl}/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          if (response.ok) {
            const data = await response.json();
            localStorage.setItem('auth_token', data.access_token);
            // Member portal reads its token from `member_token` (raw fetch layer
            // in lib/memberAuth.ts). Keep it in sync with the refreshed access
            // token so those calls don't keep sending the expired one after a
            // silent refresh.
            if (localStorage.getItem('member_token')) {
              localStorage.setItem('member_token', data.access_token);
            }
            if (data.refresh_token) {
              localStorage.setItem('refresh_token', data.refresh_token);
            }
            if (data.expires_in) {
              localStorage.setItem('token_expires_at', (Date.now() + data.expires_in * 1000).toString());
            }

            // Session recovered — re-arm the logout guard for any future failure.
            resetAuthFailureState();

            // Retry the original request with the new token, once.
            config.headers.Authorization = `Bearer ${data.access_token}`;
            config._authRetried = true;
            processQueue(null, data.access_token);
            isRefreshing = false;
            return apiClient.request(config);
          }

          // The refresh token itself was rejected (expired / revoked / replayed)
          // — it is dead, so discarding it below is correct. Any other status
          // (5xx, gateway error mid-deploy) is transient: keep the token and let
          // the next 401 try again rather than forcing a re-login.
          if (response.status === 401 || response.status === 403) {
            sessionUnrecoverable = true;
          } else {
            console.warn(`  ↳ refresh failed transiently (HTTP ${response.status}), session preserved`);
          }
          processQueue(error, null);
        } catch (refreshError) {
          // Network-level failure — transient by nature. Preserve the session.
          console.warn('  ↳ refresh request could not be sent, session preserved:', refreshError);
          processQueue(refreshError, null);
        }
        isRefreshing = false;
      } else {
        // No refresh token to fall back on: nothing can restore this session.
        sessionUnrecoverable = true;
      }
    }

    // Clear auth state ONLY when the session is genuinely unrecoverable.
    //
    // This block used to run on EVERY 401 and unconditionally removed
    // `refresh_token` along with the access token. That made recovery
    // impossible by construction: the refresh attempt above is gated on
    // `refresh_token` being present, so the first 401 destroyed the one
    // credential that could have refreshed the session. Production bore this
    // out — one account accumulated 133 valid, never-used refresh tokens while
    // /api/auth/refresh was never called once.
    if (!isTokenVerification && sessionUnrecoverable) {
      const hadMemberToken = localStorage.getItem('member_token');
      const hadAuthToken = localStorage.getItem('auth_token');
      // Captured before the removals below so the logout call can name WHICH session
      // is ending. Without it the backend can only revoke every refresh token the user
      // holds, which is how a single bad 401 in one tab used to log the same person out
      // of their phone and every other browser.
      const hadRefreshToken = localStorage.getItem('refresh_token');

      localStorage.removeItem('auth_token');
      localStorage.removeItem('member_token');
      localStorage.removeItem('session_start');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token_expires_at');

      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/signup') && !currentPath.includes('/register')) {
        // Single-flight: a burst of concurrent 401s tears the session down once.
        if (!authLogoutDispatched) {
          authLogoutDispatched = true;
          if (hadMemberToken && !hadAuthToken) {
            window.location.href = '/member/login';
          } else {
            // Hand the just-invalidated token to the logout handler. We have
            // already removed it from localStorage, so without this the
            // backend logout call would go out uncredentialed (or be skipped
            // entirely) and the token would never be blacklisted server-side.
            //
            // scope 'session': the user did not ask to be logged out, so end THIS
            // session and leave their other devices alone. `refreshToken` is what
            // tells the backend which family that is.
            window.dispatchEvent(
              new CustomEvent('auth:logout', {
                detail: {
                  reason: '401',
                  token: hadAuthToken ?? undefined,
                  refreshToken: hadRefreshToken ?? undefined,
                  scope: 'session',
                },
              })
            );
          }
        } else {
          console.warn('  ↳ auth:logout already dispatched for this failure, suppressing duplicate');
        }
      }
    }
  }
  return Promise.reject(error);
};

// Configuration for deployment retry with exponential backoff
const DEPLOYMENT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 2000, // 2s → 4s → 8s (14s total window covers Render's deploy gap)
};

// Retry interceptor for transient server errors (502/503/504 during deployments)
// Retries up to 3 times with exponential backoff before rejecting
// IMPORTANT: Does NOT call handleAuthError for transient errors to prevent token clearing
const withDeploymentRetry = (client: AxiosInstance) => (error: AxiosError) => {
  // Never sent (the session ended in another window): not a network error.
  if ((error as unknown) instanceof SessionEndedError) return Promise.reject(error);
  const config = error.config as ExtendedAxiosRequestConfig & { _retryCount?: number } | undefined;
  if (!config) return handleAuthError(error);

  const status = error.response?.status;
  const isTransient = status === 502 || status === 503 || status === 504;
  const isNetworkError = !error.response && error.code !== 'ECONNABORTED'; // No response (not timeout)

  const retryCount = config._retryCount || 0;

  if ((isTransient || isNetworkError) && retryCount < DEPLOYMENT_RETRY_CONFIG.maxRetries) {
    config._retryCount = retryCount + 1;
    const delay = DEPLOYMENT_RETRY_CONFIG.baseDelayMs * Math.pow(2, retryCount);
    console.warn(
      `🔄 Got ${status || 'network error'} (likely deployment), retry ${config._retryCount}/${DEPLOYMENT_RETRY_CONFIG.maxRetries} in ${delay / 1000}s: ${config.url}`
    );
    return new Promise((resolve) => setTimeout(resolve, delay))
      .then(() => client.request(config));
  }

  // Transient error exhausted all retries — reject WITHOUT clearing auth tokens
  if (isTransient || isNetworkError) {
    console.error(
      `❌ Transient error ${status || 'network'} persists after ${DEPLOYMENT_RETRY_CONFIG.maxRetries} retries: ${config.url}`
    );
    // Track to Datadog but do NOT call handleAuthError (which clears tokens)
    const duration = config?.metadata?.startTime
      ? Date.now() - config.metadata.startTime
      : undefined;
    captureNetworkError(
      {
        url: config?.url || 'unknown',
        method: config?.method?.toUpperCase() || 'unknown',
        status: error.response?.status,
        statusText: error.response?.statusText,
        duration,
      },
      error,
      {
        component: 'apiClient',
        action: 'deployment_retry_exhausted',
      }
    );
    return Promise.reject(error);
  }

  // Non-transient errors (401, 403, 400, etc.) go through auth error handling
  return handleAuthError(error);
};

// Apply interceptors to WeaveIn core API client
apiClient.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
apiClient.interceptors.response.use((response) => response, withDeploymentRetry(apiClient));

// Apply interceptors to Trust Marketplace referrals API client
referralsApiClient.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
referralsApiClient.interceptors.response.use((response) => response, withDeploymentRetry(referralsApiClient));

/**
 * API Error class
 */
export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: any): ApiError => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status || 500;
    const data = error.response?.data;
    const message = data?.detail || error.message || 'An error occurred';

    return new ApiError(message, status, data);
  }

  return new ApiError('An unexpected error occurred', 500, null);
};
